import React, { useMemo } from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import { formatValue, getLineColor, formatLegendName } from './chartUtils';
import WidgetState from './WidgetState';

/**
 * Top List Widget - Ranked horizontal bar list of top values
 * Like Datadog's "Top List" panel. Shows top N items sorted by value.
 */
// Display cap: configurable per widget ("Max items"), clamped so one widget
// can't render tens of thousands of DOM rows. The old hardcoded 20 silently
// truncated bigger result sets with no way around it and no doc mention.
const DEFAULT_MAX_ITEMS = 20;
const MAX_MAX_ITEMS = 500;

const TopListWidget = ({ data, config = {} }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const maxItems = Math.min(MAX_MAX_ITEMS, Math.max(1, parseInt(config.maxItems, 10) || DEFAULT_MAX_ITEMS));

  const { rankedItems, truncatedFrom } = useMemo(() => {
    const rank = (items) => ({
      rankedItems: items.sort((a, b) => b.value - a.value).slice(0, maxItems),
      truncatedFrom: items.length > maxItems ? items.length : null,
    });

    if (!data) return { rankedItems: [], truncatedFrom: null };

    // Handle timeseries data
    if (data.type === 'timeseries' && Array.isArray(data.data)) {
      return rank(data.data.map((series, index) => {
        const lastValue = series.values && series.values.length > 0
          ? parseFloat(series.values[series.values.length - 1][1])
          : 0;
        const label = formatLegendName(series.metric, config.legendTemplate);
        return { label, value: lastValue, index };
      }));
    }

    // Handle table results - each row becomes an item
    if (data.type === 'table' && data.rows && data.rows.length > 0) {
      const columns = data.columns || Object.keys(data.rows[0]);
      const labelCol = columns.find(c => typeof data.rows[0][c] === 'string');
      const valueCol = columns.find(c => typeof data.rows[0][c] === 'number');
      if (valueCol) {
        return rank(data.rows.map((row, i) => ({
          label: labelCol ? String(row[labelCol]) : `Row ${i + 1}`,
          value: typeof row[valueCol] === 'number' ? row[valueCol] : 0,
          index: i,
        })));
      }
    }

    // Handle scalar result as single item
    if (data.type === 'scalar') {
      return { rankedItems: [{ label: 'Value', value: parseFloat(data.data) || 0, index: 0 }], truncatedFrom: null };
    }

    return { rankedItems: [], truncatedFrom: null };
  }, [data, config.legendTemplate, maxItems]);

  if (rankedItems.length === 0) {
    return <WidgetState isEmpty />;
  }

  const maxValue = rankedItems[0]?.value || 1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'auto',
        p: 1.5,
        gap: 0.75,
      }}
    >
      {rankedItems.map((item, rank) => {
        const barWidth = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
        const barColor = getLineColor(rank, config.colorScheme);

        return (
          <Box
            key={rank}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              minHeight: 32,
            }}
          >
            {/* Rank number */}
            <Typography
              sx={{
                width: 22,
                textAlign: 'right',
                fontWeight: 600,
                fontSize: '0.75rem',
                color: rank < 3 ? barColor : 'text.secondary',
                flexShrink: 0,
              }}
            >
              {rank + 1}
            </Typography>

            {/* Bar + Label container */}
            <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
              {/* Background bar */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: `${barWidth}%`,
                  borderRadius: 1,
                  background: isDarkMode
                    ? alpha(barColor, 0.2)
                    : alpha(barColor, 0.12),
                  transition: 'width 0.3s ease',
                }}
              />

              {/* Label text */}
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1,
                  py: 0.5,
                  minHeight: 28,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mr: 1,
                    color: 'text.primary',
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    color: barColor,
                    flexShrink: 0,
                  }}
                >
                  {formatValue(item.value, config)}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
      {truncatedFrom && (
        <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', pt: 0.5, flexShrink: 0 }}>
          Showing top {rankedItems.length} of {truncatedFrom} — raise “Max items” in the widget settings to see more
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(TopListWidget, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.config?.legendTemplate === nextProps.config?.legendTemplate &&
    prevProps.config?.unit === nextProps.config?.unit &&
    prevProps.config?.decimalPlaces === nextProps.config?.decimalPlaces &&
    prevProps.config?.useThousandSeparator === nextProps.config?.useThousandSeparator &&
    prevProps.config?.colorScheme === nextProps.config?.colorScheme &&
    prevProps.config?.maxItems === nextProps.config?.maxItems
  );
});
